import styled from 'styled-components';
import {
  Container,
  CTA,
  Text,
  TokenTextAppearance,
  Tabs,
  Richtext,
  createTagOverride,
} from '@volkswagen-onehub/components-core';
import { useEffect, useState } from 'react';
import { Info } from '@volkswagen-onehub/icons-core';
import { Modal } from '@tribal-worldwide-london/ui-component-library-vwg';
import { ResponsiveYouTubeEmbed } from '@tribal-worldwide-london/ui-component-library-vwg';
import { transformWithEsbuild } from 'vite';

const Column = styled.div<{ padding?: string; justifyContent?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(props) => props.theme.size.dynamic0100};
  padding: ${(props) => props.padding};
  ${(props) =>
    props.justifyContent ? `justify-content: ${props.justifyContent};` : ''}
`;
const TabsWrapper = styled.div`
  padding: 0 ${(props) => props.theme.size.dynamic0100};
  @media (min-width: 960px) {
    padding: 0 ${(props) => props.theme.size.dynamic0300};
  }
`;

export interface ProductTab {
  title: string;
  titleBold: string;
  video: string;
  bodySubtitle?: string;
  body: string;
  ctaText: string;
  ctaLink: string;
}
export interface FinanceExplainedProps {
  productType: string;
  ctaAriaText: string;
  productTabs: ProductTab[];
  defaultIndex?: number;
  analyticsFunctions?: any;
  isModalVisible: any;
}

const FinanceExplained = ({
  ctaAriaText,
  productTabs,
  defaultIndex,
  analyticsFunctions,
  isModalVisible,
}: FinanceExplainedProps) => {
  const [layerVisible, setLayerVisible] = useState(true);
  isModalVisible(true);
  const handleButtonClick = () => {
    // Step 4: Call the function passed from the parent and send data
    isModalVisible(false);
  };

  const maketab = (product: ProductTab) => {
    return (
      <Column
        padding={`40px 0px`}
        key={product.title.trim() + product.titleBold.trim()}
      >
        <Text appearance={TokenTextAppearance.headline400}>
          {product?.title} <Text bold>{product?.titleBold}</Text>
        </Text>
        <ResponsiveYouTubeEmbed
          playerName="finance-explained"
          videoId={'https://www.youtube.com/watch?v=' + product.video}
          videoPauseCallback={(e: any) => {
            analyticsFunctions?.videoPauseCallback &&
              analyticsFunctions.videoPauseCallback(e, product.video);
          }}
          videoPlayCallback={(e: any) => {
            analyticsFunctions?.videoPlayCallback &&
              analyticsFunctions.videoPlayCallback(e, product.video);
          }}
          videoEndedCallback={(e: any) => {
            analyticsFunctions?.videoEndedCallback &&
              analyticsFunctions.videoEndedCallback(e, product.video);
          }}
          videoMilestoneCallback={(e: any) => {
            analyticsFunctions?.videoMilestoneCallback &&
              analyticsFunctions.videoMilestoneCallback(e, product.video);
          }}
        />

        {/* If the product has a subtitle, we'll render it differently */}
        {product?.bodySubtitle && (
          <>
            <Text appearance={TokenTextAppearance.bigcopy100} bold>
              {product?.bodySubtitle}
            </Text>

            <Text appearance={TokenTextAppearance.copy200}>
              {product?.body}
            </Text>
          </>
        )}
        {/* If it doesnt, we'll just render as markdown. */}
        {!product?.bodySubtitle && <Richtext markdown={product?.body} />}

        <Container>
          <CTA
            tag="a"
            href={product.ctaLink}
            emphasis="primary"
            aria-label={product.ctaText}
            target="_blank"
            onClick={
              analyticsFunctions?.clickFindOutMore
                ? () =>
                    analyticsFunctions.clickFindOutMore(
                      product.ctaLink,
                      product.ctaText
                    )
                : () => {}
            }
          >
            {product.ctaText}
          </CTA>
        </Container>
      </Column>
    );
  };

  const tabContent: Array<{
    title: React.ReactElement | string;
    content: React.ReactElement;
    key: string;
  }> = productTabs.reduce((content: Array<any>, tab: any) => {
    content.push({
      title: tab.title,
      content: maketab(tab),
      key: tab.title,
    });
    return content;
  }, []);

  useEffect(() => {
    layerVisible &&
      analyticsFunctions?.InfoMessageLoadForConfiguration &&
      analyticsFunctions?.InfoMessageLoadForConfiguration();
  });

  return (
    <>
      <Modal
        layerVisible={layerVisible}
        handleClose={() => {
          setLayerVisible(!layerVisible);
          handleButtonClick();
          analyticsFunctions?.closeClickForConfiguration
            ? analyticsFunctions?.closeClickForConfiguration()
            : analyticsFunctions?.closeClick && analyticsFunctions.closeClick();
        }}
        focusLayerSize="A"
      >
        <TabsWrapper>
          <Tabs
            idPrefix="fem"
            onChange={(index) => {
              analyticsFunctions?.handleTabClick &&
                analyticsFunctions.handleTabClick(productTabs[index].title);
            }}
            defaultIndex={defaultIndex || 0}
          >
            {...tabContent}
          </Tabs>
        </TabsWrapper>
      </Modal>
    </>
  );
};

export default FinanceExplained;
